import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from 'lib/api/apiClient';
import { AUTH_URLS, USER_URLS } from 'lib/api/endpoints';

export const loginUser = createAsyncThunk('auth/loginUser', async ({ email, password }, thunkAPI) => {
  try {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    const response = await apiClient.post(AUTH_URLS.login, formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    const accessToken = response?.data?.data?.access_token ?? response?.data?.access_token;
    if (!accessToken) throw new Error('No access token received');
    localStorage.setItem('token', accessToken);
    return accessToken;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Login failed');
  }
});

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (userData, thunkAPI) => {
    try {
      const response = await apiClient.post(AUTH_URLS.register, userData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Registration failed');
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(USER_URLS.getMe);
      return response?.data?.data ?? response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to fetch user');
    }
  }
);

export const logoutUser = createAsyncThunk('auth/logoutUser', async () => {
  try {
    await apiClient.post(AUTH_URLS.logout, {});
  } catch {
    // Clear local state even if API fails
  }
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userData');
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    token: localStorage.getItem('token'),
    user: null,
    isAuthenticated: !!localStorage.getItem('token'),
    loginLoading: false,
    loginError: null,
    registerLoading: false,
    registerError: null,
    userLoading: false,
    userError: null,
  },
  reducers: {
    clearLoginError(state) {
      state.loginError = null;
    },
    clearRegisterError(state) {
      state.registerError = null;
    },
    clearAuth(state) {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      localStorage.removeItem('token');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loginLoading = true;
        state.loginError = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loginLoading = false;
        state.token = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loginLoading = false;
        state.loginError = action.payload;
      })
      .addCase(registerUser.pending, (state) => {
        state.registerLoading = true;
        state.registerError = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.registerLoading = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.registerLoading = false;
        state.registerError = action.payload;
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        state.userLoading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.userLoading = false;
        state.user = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.userLoading = false;
        state.userError = action.payload;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
      });
  },
});

export const { clearLoginError, clearRegisterError, clearAuth } = authSlice.actions;
export default authSlice.reducer;
